import React, { useState, useEffect, useRef } from 'react';
import { useToast } from '../../context/ToastContext';
import { 
  Plus as PlusIcon, 
  Trash2 as Trash2Icon, 
  Edit as EditIcon, 
  Save as SaveIcon, 
  X as XIcon, 
  Image as ImageIcon, 
  Search as SearchIcon, 
  Upload as UploadIcon 
} from 'lucide-react';
import './AdminPanel.css';
import { 
  TextField, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Checkbox, 
  FormControlLabel,
  Typography,
  Box,
  Paper,
  Divider,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import * as ProductService from '../../services/ProductService';
import { DIRECT_PRODUCTS } from '../../data/ProductData';

const AdminPanel = () => {
  const { showSuccessToast, showErrorToast } = useToast();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [isEditingProduct, setIsEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newProduct, setNewProduct] = useState({
    name: '',
    category: '',
    price: '',
    image: '',
    imageFile: null,
    description: '',
    featured: false,
    inStock: true,
    discount: 0,
    weight: '',
    pageLocation: []
  });
  const [isUploading, setIsUploading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  
  // File input reference
  const fileInputRef = useRef(null);

  // Categories for dropdown
  const categories = [
    'rings',
    'pendants',
    'earrings',
    'bracelets',
    'necklaces',
    'watches'
  ];

  // Page locations for filter dropdown
  const pageLocations = [
    'collections',
    'new-arrivals',
    'special-offers'
  ];

  // Load products from direct data
  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      
      // Try to get products from API first
      try {
        const apiProducts = await ProductService.getAllProducts();
        console.log("Loaded products from API:", apiProducts.length);
        
        if (apiProducts && apiProducts.length > 0) {
          setProducts(apiProducts);
          setFilteredProducts(apiProducts);
          setLoadingProducts(false);
          return;
        }
      } catch (apiError) {
        console.error("Error loading from API, falling back to direct products:", apiError);
      }
      
      // Fallback to direct products if API fails
      const allProducts = [...DIRECT_PRODUCTS].map(product => ({
        ...product,
        inStock: true, // Ensure all products are marked as in stock
        pageLocation: product.pageLocation || [], // Ensure pageLocation is always an array
        description: product.description || '', // Ensure description is never null
        image: product.image || '/media/placeholder.jpeg' // Ensure image is never null
      }));
      
      setProducts(allProducts);
      setFilteredProducts(allProducts);
      
    } catch (error) {
      console.error('Error loading products:', error);
      showErrorToast('Failed to load products.');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Load products on component mount
  useEffect(() => {
    loadProducts();
  }, []);

  // Load products directly from the API
  const loadDirectProducts = async () => {
    try {
      setLoadingProducts(true);
      
      // Get products from API
      const apiProducts = await ProductService.getAllProducts();
      console.log("Total products from API:", apiProducts.length);
      
      if (apiProducts && apiProducts.length > 0) {
        // Process products to ensure all required fields are present
        const processedProducts = apiProducts.map(product => {
          // Fix image path by removing the leading dot if it exists and ensuring it starts with a forward slash
          let imagePath = product.image;
          if (imagePath) {
            if (imagePath.startsWith('./')) {
              imagePath = imagePath.substring(2);
            }
            if (!imagePath.startsWith('/') && !imagePath.startsWith('http')) {
              imagePath = '/' + imagePath;
            }
          }
          
          return {
            ...product,
            inStock: Boolean(product.inStock),
            featured: Boolean(product.featured),
            pageLocation: product.pageLocation || [],
            description: product.description || '',
            image: imagePath || '/media/placeholder.jpeg'
          };
        });
        
        console.log("Processed products:", processedProducts.length);
        
        setProducts(processedProducts);
        setFilteredProducts(processedProducts);
        showSuccessToast(`Successfully loaded ${processedProducts.length} products from API!`);
      } else {
        // Fallback to direct products
        console.warn("No products found in API, falling back to direct products");
        const directProducts = [...DIRECT_PRODUCTS].map(product => ({
          ...product,
          inStock: true,
          pageLocation: product.pageLocation || [],
          description: product.description || '',
          image: product.image || '/media/placeholder.jpeg'
        }));
        
        setProducts(directProducts);
        setFilteredProducts(directProducts);
        showSuccessToast(`Loaded ${directProducts.length} products from local data!`);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      showErrorToast('Failed to load products: ' + error.message);
      
      // Fallback to direct products on error
      const directProducts = [...DIRECT_PRODUCTS].map(product => ({
        ...product,
        inStock: true,
        pageLocation: product.pageLocation || [],
        description: product.description || '',
        image: product.image || '/media/placeholder.jpeg'
      }));
      
      setProducts(directProducts);
      setFilteredProducts(directProducts);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Filter products when search term changes
  useEffect(() => {
    if (!Array.isArray(products) || products.length === 0) return;
    
    if (searchTerm.trim() === '') {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(
        product => 
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  // Handle input change for new/edited product
  const handleInputChange = (e) => {
    const { name, value, type, checked, selectedOptions } = e.target;
    
    // Handle multiple select for pageLocation
    if (name === 'pageLocation' && selectedOptions) {
      const selectedValues = Array.from(selectedOptions).map(option => option.value);
      const hasCollections = selectedValues.includes('collections');
      const finalValues = hasCollections ? ['collections'] : selectedValues;
      
      if (isEditingProduct !== null) {
        const updatedProducts = [...products];
        updatedProducts[isEditingProduct] = {
          ...updatedProducts[isEditingProduct],
          [name]: finalValues
        };
        setProducts(updatedProducts);
      } else {
        setNewProduct({
          ...newProduct,
          [name]: finalValues
        });
      }
      return;
    }
    
    const inputValue = type === 'checkbox' ? checked : value;
    
    if (isEditingProduct !== null) {
      const updatedProducts = [...products];
      updatedProducts[isEditingProduct] = {
        ...updatedProducts[isEditingProduct],
        [name]: inputValue
      };
      setProducts(updatedProducts);
    } else {
      setNewProduct({
        ...newProduct,
        [name]: inputValue
      });
    }
  };

  // Image optimization function
  const optimizeImage = async (file) => {
    try {
      setIsUploading(true);
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;
          img.onload = () => {
            try {
              // Create canvas for image resizing
              const canvas = document.createElement('canvas');
              // Max width/height for the image (800px is sufficient for product images)
              const MAX_WIDTH = 800;
              const MAX_HEIGHT = 800;
              
              let width = img.width;
              let height = img.height;
              
              // Resize image if it's too large
              if (width > MAX_WIDTH || height > MAX_HEIGHT) {
                if (width > height) {
                  height = Math.round(height * (MAX_WIDTH / width));
                  width = MAX_WIDTH;
                } else {
                  width = Math.round(width * (MAX_HEIGHT / height));
                  height = MAX_HEIGHT;
                }
              }
              
              canvas.width = width;
              canvas.height = height;
              
              const ctx = canvas.getContext('2d');
              // Use a white background to prevent transparency issues
              ctx.fillStyle = '#FFFFFF';
              ctx.fillRect(0, 0, width, height);
              ctx.drawImage(img, 0, 0, width, height);
              
              // Get the resized image as base64 string with reduced quality (0.7 = 70% quality)
              // Using JPEG format for better compression
              const optimizedImageData = canvas.toDataURL('image/jpeg', 0.7);
              resolve(optimizedImageData);
            } catch (error) {
              console.error('Error optimizing image:', error);
              reject(error);
            }
          };
          img.onerror = (error) => {
            console.error('Error loading image:', error);
            reject(error);
          };
        };
        reader.onerror = (error) => {
          console.error('Error reading file:', error);
          reject(error);
        };
      });
    } catch (error) {
      console.error('Error in image optimization:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file change
  const handleFileChange = async (event) => {
    try {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        showErrorToast('Image size must be less than 10MB');
        return;
      }

      const optimizedImage = await optimizeImage(file);
      
      if (isEditingProduct !== null) {
        const updatedProducts = [...products];
        updatedProducts[isEditingProduct] = {
          ...updatedProducts[isEditingProduct],
          image: optimizedImage
        };
        setProducts(updatedProducts);
      } else {
        setNewProduct(prev => ({
          ...prev,
          image: optimizedImage
        }));
      }
    } catch (error) {
      console.error('Error handling file:', error);
      showErrorToast('Failed to process image. Please try again.');
    }
  };

  // Trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Handle product deletion
  const handleDelete = async (productId) => {
    try {
      // Delete from direct products
      const success = ProductService.deleteProduct(productId);
      
      if (success) {
        // Update local state
        const updatedProducts = products.filter(product => product.id !== productId);
        setProducts(updatedProducts);
        setFilteredProducts(updatedProducts.filter(product => 
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
        ));
        showSuccessToast('Product deleted successfully');
      } else {
        showErrorToast('Product not found');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      showErrorToast('Failed to delete product. Please try again.');
    }
  };

  // Handle adding a new product
  const handleAddProduct = async () => {
    try {
      // Validate required fields
      if (!newProduct.name || !newProduct.category || !newProduct.price) {
        showErrorToast('Please fill in all required fields: Name, Category, and Price');
        return;
      }

      // Validate price is a number
      if (isNaN(parseFloat(newProduct.price))) {
        showErrorToast('Price must be a valid number');
        return;
      }

      setIsUploading(true);

      // Handle image upload if there's a file
      let imageUrl = newProduct.image;
      if (newProduct.imageFile) {
        try {
          const reader = new FileReader();
          const filePromise = new Promise((resolve, reject) => {
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = (e) => reject(e);
          });
          reader.readAsDataURL(newProduct.imageFile);
          imageUrl = await filePromise;
        } catch (error) {
          console.error('Error reading image file:', error);
          showErrorToast('Failed to process image. Please try again.');
          setIsUploading(false);
          return;
        }
      }

      // Prepare product data
      const productData = {
        ...newProduct,
        image: imageUrl,
        price: parseFloat(newProduct.price),
        discount: parseFloat(newProduct.discount || 0),
        // Ensure pageLocation is an array
        pageLocation: Array.isArray(newProduct.pageLocation) ? newProduct.pageLocation : []
      };
      
      // Remove imageFile property as it's not needed for API
      delete productData.imageFile;

      console.log('Adding product with data:', productData);

      // Add product via service
      const addedProduct = await ProductService.addProduct(productData);
      
      // Update products list
      setProducts([addedProduct, ...products]);
      setFilteredProducts([addedProduct, ...filteredProducts]);
      
      // Reset form and state
      setNewProduct({
        name: '',
        category: '',
        price: '',
        image: '',
        imageFile: null,
        description: '',
        featured: false,
        inStock: true,
        discount: 0,
        weight: '',
        pageLocation: []
      });
      
      setIsAddingProduct(false);
      showSuccessToast('Product added successfully!');
    } catch (error) {
      console.error('Error adding product:', error);
      showErrorToast(`Failed to add product: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Save edited product
  const handleSaveEdit = async () => {
    try {
      if (isEditingProduct === null) return;
      
      const editedProduct = products[isEditingProduct];
      if (!editedProduct.name || !editedProduct.category || !editedProduct.price) {
        showErrorToast('Please fill in all required fields');
        return;
      }

      const updatedProduct = await ProductService.updateProduct(editedProduct.id, editedProduct);
      
      // Update local state
      const updatedProducts = [...products];
      updatedProducts[isEditingProduct] = updatedProduct;
      setProducts(updatedProducts);
      
      setIsEditingProduct(null);
      showSuccessToast('Product updated successfully');
    } catch (error) {
      console.error('Error updating product:', error);
      showErrorToast('Failed to update product');
    }
  };

  // Cancel adding/editing
  const handleCancel = () => {
    if (isEditingProduct !== null) {
      setIsEditingProduct(null);
    } else {
      setNewProduct({
        name: '',
        category: '',
        price: '',
        image: '',
        imageFile: null,
        description: '',
        featured: false,
        inStock: true,
        discount: 0,
        weight: '',
        pageLocation: []
      });
      setIsAddingProduct(false);
    }
  };

  if (loadingProducts) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="admin-panel">
      {isOffline && (
        <div className="offline-banner" style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          ⚠️ You are currently offline. Some features may be limited.
          <button 
            onClick={() => {
              const sampleProducts = ProductService.getSampleProducts();
              ProductService.manuallyAddProducts(sampleProducts);
              loadProducts();
              showSuccessToast('Sample products loaded successfully!');
            }}
            style={{
              marginLeft: '10px',
              backgroundColor: '#856404',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '5px 10px',
              cursor: 'pointer'
            }}
          >
            Load Sample Products
          </button>
        </div>
      )}

      <div className="admin-header">
        <h1>Admin Panel</h1>
        <p>Manage your products and website content</p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={loadDirectProducts}
            style={{
              marginTop: '10px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Load All Products
          </button>
        </div>
      </div>

      <div className="admin-actions">
        <div className="search-container">
          <SearchIcon className="search-icon" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <button 
          className="add-product-btn"
          onClick={() => setIsAddingProduct(true)}
          disabled={isAddingProduct || isEditingProduct !== null}
        >
          <PlusIcon size={18} /> Add New Product
        </button>
      </div>

      {isAddingProduct && (
        <Paper className="product-form-container" elevation={3}>
          <Typography variant="h4" component="h2" className="form-title">
            Add New Product
          </Typography>
          <Divider sx={{ mb: 4, opacity: 0.2 }} />
          
          <Box className="product-form">
            <Box className="form-row">
              <TextField
                label="Product Name"
                variant="outlined"
                fullWidth
                required
                id="name"
                name="name"
                value={newProduct.name}
                onChange={handleInputChange}
                margin="normal"
                className="form-field"
                InputLabelProps={{
                  shrink: true,
                }}
              />
              
              <FormControl fullWidth margin="normal" className="form-field">
                <InputLabel id="category-label" shrink>Category</InputLabel>
                <Select
                  labelId="category-label"
                  id="category"
                  name="category"
                  value={newProduct.category}
                  onChange={handleInputChange}
                  label="Category"
                  required
                  displayEmpty
                >
                  <MenuItem value="">Select Category</MenuItem>
                  {categories.map(category => (
                    <MenuItem key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box className="form-row">
              <TextField
                label="Price"
                variant="outlined"
                fullWidth
                required
                id="price"
                name="price"
                value={newProduct.price}
                onChange={handleInputChange}
                placeholder="1200"
                margin="normal"
                className="form-field"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              
              <TextField
                label="Discount"
                variant="outlined"
                fullWidth
                type="number"
                id="discount"
                name="discount"
                value={newProduct.discount}
                onChange={handleInputChange}
                margin="normal"
                className="form-field"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: 0,
                  max: 100
                }}
              />
            </Box>
            
            <Box className="form-row">
              <TextField
                label="Weight"
                variant="outlined"
                fullWidth
                id="weight"
                name="weight"
                value={newProduct.weight}
                onChange={handleInputChange}
                margin="normal"
                className="form-field"
                placeholder="e.g., 4g"
                InputLabelProps={{
                  shrink: true,
                }}
              />
              
              <Box className="image-upload-container form-field">
                <Button 
                  variant="outlined" 
                  onClick={triggerFileInput}
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  {newProduct.image ? 'Change Image' : 'Upload Image'}
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  accept="image/*"
                />
                {newProduct.imagePreview && (
                  <div className="image-preview">
                    <img src={newProduct.imagePreview} alt="Preview" />
                  </div>
                )}
              </Box>
            </Box>
            
            <Box className="form-row">
              <FormControl fullWidth margin="normal" className="form-field">
                <InputLabel id="page-location-label" shrink>Page Location</InputLabel>
                <Select
                  labelId="page-location-label"
                  id="pageLocation"
                  name="pageLocation"
                  multiple
                  value={newProduct.pageLocation}
                  onChange={handleInputChange}
                  label="Page Location"
                  renderValue={(selected) => selected.map(value => 
                    value.charAt(0).toUpperCase() + value.slice(1).replace('-', ' ')
                  ).join(', ')}
                >
                  {pageLocations.map(location => (
                    <MenuItem key={location} value={location}>
                      {location.charAt(0).toUpperCase() + location.slice(1).replace('-', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              id="description"
              name="description"
              value={newProduct.description}
              onChange={handleInputChange}
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
            />
            
            <Box className="form-row checkbox-row">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newProduct.featured}
                    onChange={handleInputChange}
                    name="featured"
                    color="primary"
                  />
                }
                label="Featured Product"
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newProduct.inStock}
                    onChange={handleInputChange}
                    name="inStock"
                    color="primary"
                  />
                }
                label="In Stock"
              />
            </Box>
            
            <Box className="form-actions">
              <Button 
                variant="outlined" 
                color="error" 
                onClick={handleCancel}
                startIcon={<XIcon />}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleAddProduct}
                startIcon={<SaveIcon />}
              >
                Save Product
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      <div className="products-container">
        <h2>Products ({filteredProducts.length})</h2>
        
        {filteredProducts.length === 0 ? (
          <div className="no-products">
            <ImageIcon size={48} />
            <p>No products found</p>
            {searchTerm && <p>Try a different search term</p>}
          </div>
        ) : (
          <div className="products-table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Page Locations</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product, index) => (
                  <tr key={product.id} className={isEditingProduct === index ? 'editing' : ''}>
                    <td className="product-image-cell">
                      <img 
                        src={product.image || '/media/placeholder.jpeg'} 
                        alt={product.name}
                        onError={(e) => {
                          console.log('Image load error for:', product.name, 'URL:', e.target.src);
                          
                          // Try different image path variations
                          const originalSrc = e.target.src;
                          
                          // If the image URL is relative, try with domain
                          if (originalSrc.startsWith('/')) {
                            const withDomain = `https://nmdiamond.in${originalSrc}`;
                            console.log('Trying with domain:', withDomain);
                            e.target.src = withDomain;
                            return;
                          }
                          
                          // If the image URL has domain but fails, try without domain
                          if (originalSrc.includes('nmdiamond.in') && originalSrc.includes('/media/')) {
                            const withoutDomain = originalSrc.substring(originalSrc.indexOf('/media/'));
                            console.log('Trying without domain:', withoutDomain);
                            e.target.src = withoutDomain;
                            return;
                          }
                          
                          // If all else fails, use placeholder
                          if (e.target.src !== '/media/placeholder.jpeg') {
                            console.log('Using placeholder image');
                            e.target.src = '/media/placeholder.jpeg';
                          }
                        }}
                        style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'contain' }}
                      />
                    </td>
                    <td>
                      {isEditingProduct === index ? (
                        <input
                          type="text"
                          name="name"
                          value={product.name}
                          onChange={handleInputChange}
                          required
                        />
                      ) : (
                        product.name
                      )}
                    </td>
                    <td>
                      {isEditingProduct === index ? (
                        <div className="form-group">
                          <label htmlFor="category">Category</label>
                          <select
                            className="form-control"
                            id="category"
                            name="category"
                            value={product.category || ''}
                            onChange={handleInputChange}
                          >
                            <option value="">Select Category</option>
                            <option value="rings">Rings</option>
                            <option value="earrings">Earrings</option>
                            <option value="necklaces">Necklaces</option>
                            <option value="bracelets">Bracelets</option>
                          </select>
                        </div>
                      ) : (
                        product.category 
                          ? product.category.charAt(0).toUpperCase() + product.category.slice(1) 
                          : 'Uncategorized'
                      )}
                    </td>
                    <td>
                      {isEditingProduct === index ? (
                        <input
                          type="text"
                          name="price"
                          value={product.price}
                          onChange={handleInputChange}
                          required
                        />
                      ) : (
                        product.price
                      )}
                    </td>
                    <td>
                      {isEditingProduct === index ? (
                        <div className="stock-toggle-container">
                          <button 
                            className={`stock-toggle-btn ${product.inStock ? 'in-stock' : 'out-of-stock'}`}
                            onClick={() => {
                              const updatedProducts = [...products];
                              updatedProducts[isEditingProduct] = {
                                ...updatedProducts[isEditingProduct],
                                inStock: !updatedProducts[isEditingProduct].inStock
                              };
                              setProducts(updatedProducts);
                            }}
                          >
                            {product.inStock ? 'In Stock' : 'Out of Stock'}
                          </button>
                        </div>
                      ) : (
                        <span className={`status-badge ${product.inStock ? 'in-stock' : 'out-of-stock'}`}>
                          {product.inStock ? 'In Stock' : 'Out of Stock'}
                        </span>
                      )}
                    </td>
                    <td>
                      {isEditingProduct === index ? (
                        <div className="edit-select-container">
                          <select
                            name="pageLocation"
                            value={Array.isArray(product.pageLocation) ? product.pageLocation : []}
                            onChange={handleInputChange}
                            multiple
                            className="page-location-select"
                          >
                            {pageLocations.map(location => (
                              <option key={location} value={location}>
                                {location.charAt(0).toUpperCase() + location.slice(1).replace('-', ' ')}
                              </option>
                            ))}
                          </select>
                          <small className="select-help-text">Hold Ctrl/Cmd to select multiple</small>
                        </div>
                      ) : (
                        <div className="page-locations">
                          {Array.isArray(product.pageLocation) && product.pageLocation.length > 0 ? 
                            product.pageLocation.map(location => (
                              <span key={location} className="page-location-badge">
                                {location.charAt(0).toUpperCase() + location.slice(1).replace('-', ' ')}
                              </span>
                            )) : 
                            <span className="no-location">None</span>
                          }
                        </div>
                      )}
                    </td>
                    <td className="actions-cell">
                      {isEditingProduct === index ? (
                        <>
                          <button className="action-btn save-btn" onClick={handleSaveEdit}>
                            <SaveIcon size={18} />
                          </button>
                          <button className="action-btn cancel-btn" onClick={handleCancel}>
                            <XIcon size={18} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button 
                            className="action-btn edit-btn"
                            onClick={() => setIsEditingProduct(index)}
                            disabled={isAddingProduct || isEditingProduct !== null}
                          >
                            <EditIcon size={18} />
                          </button>
                          <button 
                            className="action-btn delete-btn"
                            onClick={() => handleDelete(product.id)}
                            disabled={isAddingProduct || isEditingProduct !== null}
                          >
                            <Trash2Icon size={18} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel; 